import { isEmpty, isNotDefined, isNotEmpty } from '../isEmpty'
import { isTrue } from '../boolean'
import {
  isAttachmentId,
  type CustomFieldData,
  type CustomFieldFormat,
  type CustomFieldType,
  type CustomFieldTypeName,
  type CustomFieldValue,
  type CustomTableType,
} from '../../model'

export function toReadableTableType(tableType: CustomTableType): string {
  return tableType.replace(/([A-Z])/g, ' $1').trim()
}

export function toDisplayType(fieldType: CustomFieldTypeName): string {
  switch (fieldType) {
    case 'attachment':
      return 'Attachment'
    case 'boolean':
      return 'Yes/No'
    case 'calculation':
      return 'Calculation'
    case 'date':
      return 'Date'
    case 'multipleChoice':
      return 'Multiple Choice'
    case 'number':
      return 'Number'
    case 'string':
      return 'Text'
    case 'url':
      return 'URL'
    default:
      return fieldType
  }
}

export const getDateValue = (value?: CustomFieldValue): Date => {
  const dateValue = findDateValue(value)
  if (dateValue) return dateValue
  return new Date()
}

export const findDateValue = (value?: CustomFieldValue): Date | undefined => {
  if (Array.isArray(value)) return findDateValue(value[0])
  if (typeof value === 'number') return new Date(value)
  if (typeof value === 'string') return new Date(value)
}

export const getStringValue = (value?: CustomFieldValue): string => {
  if (Array.isArray(value)) {
    return value
      .map((item) => getStringValue(item))
      .filter(isNotEmpty)
      .join(', ')
  }
  if (isEmpty(value)) return ''
  if (typeof value === 'number') return value.toString()
  if (typeof value === 'string') return value
  return ''
}

export const getNumberValue = (value?: CustomFieldValue): number | string => {
  if (Array.isArray(value)) return getNumberValue(value[0])
  if (typeof value === 'number') return value
  if (isEmpty(value)) return ''
  if (typeof value === 'string') return Number(value)
  return ''
}

export const toCustomFieldDisplayValue = (field: CustomFieldData, value?: CustomFieldValue): string => {
  if (Array.isArray(value)) {
    return value.map((item) => toCustomFieldDisplayValue(field, item)).join(', ')
  }
  switch (field.fieldType) {
    case 'boolean':
      if (isNotDefined(value)) return '-'
      return isTrue(value) ? 'Yes' : 'No'
    case 'number':
    case 'calculation':
      return formatNumber(value?.toString(), field.format)
    case 'string':
    case 'url':
      return value?.toString() || '-'
    case 'date':
      return toDateDisplay(value)
    case 'attachment':
      if (isAttachmentId(value)) return value
      return '-'
    default:
      return value?.toString() || '-'
  }
}

export const formatCustomFieldValue = (value: CustomFieldType, format?: CustomFieldFormat): string => {
  if (isNotDefined(value)) return '-'
  if (!format) return value.toString()
  if (typeof value === 'number') return formatNumber(value.toString(), format, '-')
  if (typeof value === 'string') return formatNumber(value, format, '-')
  return value.toString()
}

const formatNumber = (value: string | undefined, format?: CustomFieldFormat, fallback: string = 'N/A'): string => {
  if (isNotDefined(value)) return fallback
  if (!format) return value.toString() || fallback
  const { prefix = '', suffix = '', decimalPlaces = 0, delimiter = ',', separator = '.' } = format

  const numberValue = Number(value)
  if (Number.isNaN(numberValue)) return value.toString() || fallback

  const setDecimals = numberValue.toFixed(decimalPlaces)
  const formatted: string[] = setDecimals.split('')

  const amountOfDelimiters = Math.ceil((setDecimals.length - decimalPlaces) / 3) - 1

  if (delimiter) {
    for (let i = 0; i < amountOfDelimiters; i++) {
      const precisionOffset = decimalPlaces
      const separatorOffset = decimalPlaces ? 1 : 0
      const thousandOffset = 3 + (delimiter ? 1 : 0)
      const delimiterPosition = -precisionOffset - separatorOffset - i * thousandOffset - 3

      if (delimiterPosition * -1 >= formatted.length) continue

      formatted.splice(delimiterPosition, 0, delimiter)
    }
  }

  return `${prefix}${formatted.join('')}${suffix}`
}

const toDateDisplay = (value?: CustomFieldType): string => {
  if (isEmpty(value)) return '-'
  if (typeof value === 'number') return new Date(value).toString()
  if (typeof value === 'string') return new Date(value).toString()
  return '-'
}
